import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { Divider, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import StringResourceManager, { GetString } from '../StringResourceManager';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Cron } from 'react-js-cron';
import 'react-js-cron/dist/styles.css';
import { convertDateYYYYMMDD, maskString } from '../../utils/utils';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import {
  LogFileEntry,
  LogsFolderInfo,
  UploadLogsDirectories,
} from '../../Interfaces/LogsInfo/LogsFolderInfo';
import {
  Grid as FileGrid,
  GridCellProps,
  GridColumn,
  GridToolbar,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid';
import {
  Input,
  InputChangeEvent,
  InputPrefix,
  InputSuffix,
  TextBox,
} from '@progress/kendo-react-inputs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button } from '@progress/kendo-react-buttons';
import clsx from 'clsx';
import { getDateCreatedScheduledCommandByBatchList } from '../../store/Commands/CommandsAction';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Checkbox, FormControlLabel } from '@mui/material';
import {
  AutoComplete,
  AutoCompleteChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { clearSuccessMessage } from '../../store/Assets/AssetsActions';
import ScheduledCommandHistory from '../CommandsHistory/ScheduledCommandHistory';
import ScheduledCommandNodeSelectionDialog from './ScheduledCommandNodeSelectionDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logsContainer: {
      display: 'flex',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
    span: {
      fontWeight: 'bold',
      fontSize: '1rem',
      margin: '10px',
    },
    margin: {
      margin: theme.spacing(1),
    },
    copyToClipBoard: {
      fontSize: '1.5rem',
    },
    copyToClipBoardGrid: { marginTop: 22, marginLeft: 20 },
    buttonStyle: { marginLeft: '10px' },
    dateRangePickerGrid: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
    },
    leftMargin: {
      marginLeft: '12px',
    },
    datePicker: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    startDateMargin: {
      marginTop: '2px',
    },
    gridItemMargin: {
      marginRight: '144px',
    },
    gridStyle: {
      maxHeight: 400,
      marginLeft: '10px',
      marginBottom: '10px',
    },
    actionButton: {
      display: 'flex',
      justifyContent: 'center',
      padding: '12px 16px',
      borderTop: '1px solid #e5e5e5',
      backgroundColor: '#fafafa',
      width: '800px',
      marginLeft: '10px',
      marginBottom: '10px',
    },
  })
);

const INVALID_CHARS = /[<>"|?*]/;
const FORWARD_SLASH = /\//;
export const isValidDirectory = (raw?: string | null) => {
  const name = (raw ?? '').trim();
  if (!name) return false;
  if (INVALID_CHARS.test(name)) return false;
  if (FORWARD_SLASH.test(name)) return false;
  const parts = name.split('\\');
  for (const seg of parts) {
    if (!isValidDirSegment(seg)) return false;
  }
  return true;
};

function isValidDirSegment(segment: string): boolean {
  if (!segment) return false;
  if (segment === '.' || segment === '..') return false;
  const last = segment[segment.length - 1];
  if (last === ' ' || last === '.') return false;
  if (INVALID_CHARS.test(segment)) return false;
  if (FORWARD_SLASH.test(segment)) return false;
  return true;
}

export const DirectoryCell = (props: GridCellProps) => {
  const uploadLogsDirectories: UploadLogsDirectories = useSelector(
    (state: ApplicationState) => state.logs.uploadLogsDirectories
  );
  const isFetching: boolean = useSelector(
    (state: ApplicationState) => state.logs.isFetchingUploadLogsDirs
  );
  const { dataItem, field = 'DirectoryName' } = props;
  const value = ((dataItem as any)[field] ?? '') as string;

  const [valid, setValid] = React.useState<boolean>(isValidDirectory(value));

  React.useEffect(() => {
    const ok = isValidDirectory(value);
    setValid(ok);
  }, [value]);

  const handleChange = (e: AutoCompleteChangeEvent) => {
    const ok = isValidDirectory(e.value);
    setValid(ok);

    props.onChange?.({
      dataItem,
      field,
      value: e.value,
    } as GridItemChangeEvent);
  };

  return (
    <td>
      <AutoComplete
        loading={isFetching}
        data={uploadLogsDirectories.DirectoryNames}
        value={value}
        onChange={handleChange}
        valid={valid}
        style={{
          width: '100%',
        }}
      />
    </td>
  );
};

export const FileNameCell = (props: GridCellProps) => {
  const { dataItem, field = 'FileName' } = props;
  const value = (dataItem as any)[field] ?? '';

  return (
    <td>
      <Input
        value={value}
        onChange={(e: InputChangeEvent) =>
          props.onChange?.({
            dataItem,
            field,
            value: e.value,
          } as GridItemChangeEvent)
        }
        style={{ width: '100%' }}
        placeholder="eg. Robot.tra;vpos.tra;*.tra"
      />
    </td>
  );
};

export default function ScheduledLogsContainer() {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const commandName: string = 'ScheduleUploadLogs';

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const [commandSend, displayCommandSend] = React.useState(false);
  const [historyStartDate, setHistoryStartDate] = React.useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [historyEndDate, setHistoryEndDate] = React.useState<Date>(new Date());
  const [useCurrentDate, setUseCurrentDate] = React.useState<boolean>(true);

  const logsUploadPath: LogsFolderInfo = useSelector(
    (state: ApplicationState) => state.logs.folderInfo
  );

  const commandSuccessMessage: string = useSelector(
    (state: ApplicationState) => state.assetsReducer.commandSuccessMessage
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  React.useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const ADD_NEW = GetString('ADD_NEW_STR');
  const SEND_SCHEDULE_LOGS_STR = GetString('SEND_SCHEDULE_LOGS_STR');
  const REMOVE_STR = GetString('REMOVE_STR');

  const [startDate, setStartDate] = React.useState<Date | null>(new Date());
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [showEndDate, setShowEndDate] = React.useState(false);
  const [nodeSelectionDialogVisible, setNodeSelectionDialogVisible] =
    React.useState(false);
  const [parameters, setParameters] = React.useState<string>('');
  const DATA_ITEM_KEY = 'EntryId';
  const [logFilesData, setLogFilesData] = React.useState<LogFileEntry[]>([]);
  const [cronFrequency, setCronFrequency] = React.useState<string>('0 0 * * *');

  const itemChange = (event: GridItemChangeEvent) => {
    const { dataItem, field, value } = event;
    if (!field) return;

    setLogFilesData(prev =>
      prev.map(row =>
        (row as any)[DATA_ITEM_KEY] === (dataItem as any)[DATA_ITEM_KEY]
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const generateFilePaths = (logFiles: LogFileEntry[]): string => {
    const paths = new Set<string>();

    for (const { DirectoryName, FileName } of logFiles) {
      const dir = (DirectoryName ?? '').trim();

      if (!dir) continue;

      const fileNames = (FileName ?? '')
        .split(';')
        .map(n => n.trim())
        .filter(n => n.length > 0);

      for (const name of fileNames) {
        const sep = dir.endsWith('\\') ? '' : '\\';
        const path = `${dir}${sep}${name}`;
        if (path.trim().length > 0) {
          paths.add(path);
        }
      }
    }
    return Array.from(paths).join(';');
  };

  const getParameters = () => {
    return `<ScheduleUploadLogs><Path>${
      logsUploadPath.Path
    }</Path><Files>${generateFilePaths(
      logFilesData
    ).trim()}</Files></ScheduleUploadLogs>`;
  };

  const resetSuccessMessage = () => {
    clearSuccessMessage(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;

    timeoutId = setTimeout(() => {
      getDateCreatedScheduledCommandByBatchList(
        dispatch,
        commandName,
        historyStartDate.toUTCString(),
        useCurrentDate ? new Date().toUTCString() : historyEndDate.toUTCString()
      );
      displayCommandSend(false);
      resetSuccessMessage();
    }, 3000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandSuccessMessage]);

  const [showCopyToClipBoard, setShowCopyToClipBoard] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    let timeoutId: any;
    if (showCopyToClipBoard) {
      timeoutId = setTimeout(() => {
        setShowCopyToClipBoard(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showCopyToClipBoard]);

  React.useEffect(() => {
    if (logFilesData.length === 0) {
      const newDataItem = {
        [DATA_ITEM_KEY]: generateId(logFilesData),
        DirectoryName: '',
        FileName: '',
      };

      setLogFilesData([...logFilesData, newDataItem]);
    }
  }, [logFilesData]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowEndDate(e.target.checked);
    if (!e.target.checked) setEndDate(null);
    else setEndDate(new Date());
  };

  const generateId = (data: LogFileEntry[]) =>
    data.reduce((acc, current) => Math.max(acc, current.EntryId), 0) + 1;

  const handleAddFileRow = () => {
    const newDataItem = {
      [DATA_ITEM_KEY]: generateId(logFilesData),
      DirectoryName: '',
      FileName: '',
    };

    setLogFilesData([...logFilesData, newDataItem]);
  };

  const handleRemoveFileRow = (index: number) => {
    const updatedFiles = logFilesData.filter((_, i) => i !== index);
    setLogFilesData(updatedFiles);
  };

  const ActionCell = (props: GridCellProps) => {
    return (
      <td style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          themeColor="error"
          onClick={() => handleRemoveFileRow(props.rowDataIndex!)}
        >
          {REMOVE_STR}
        </Button>
      </td>
    );
  };

  const hasInvalidDirectoryPath = (logFiles: LogFileEntry[]): boolean => {
    return logFiles.some(entry => {
      const dir = (entry.DirectoryName ?? '').trim();
      const fileName = (entry.FileName ?? '').trim();
      if (!dir || !fileName) return true;
      return !isValidDirectory(dir);
    });
  };

  const handleCronChange = (newCronValue: string) => {
    setCronFrequency(newCronValue);
  };

  const handleSendScheduleLogs = () => {
    setParameters(getParameters());
    setNodeSelectionDialogVisible(true);
  };

  const onHistoryStartDateChange = (date: Date) => {
    setHistoryStartDate(date);
  };

  const onHistoryEndDateChange = (date: Date) => {
    setHistoryEndDate(date);
  };

  const onUseCurrentDateTimeFilterChange = (useCurrentDate: boolean) => {
    setUseCurrentDate(useCurrentDate);
  };

  useEffect(() => {
    if (!nodeSelectionDialogVisible) {
      setLogFilesData([{ EntryId: 1, DirectoryName: '', FileName: '' }]);
    }
  }, [nodeSelectionDialogVisible]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Grid className={classes.margin}>
            <StringResourceManager
              variant="body1"
              style="commandLabel"
              stringKey="SCHEDULED_LOGS_HEADER"
              gutterBottom={true}
            />
          </Grid>

          <Grid className={classes.margin}>
            <TextBox
              value={maskString(logsUploadPath.Path)}
              readOnly={true}
              prefix={() => (
                <>
                  <InputPrefix style={{ backgroundColor: '#EBEBE4' }}>
                    <span className={classes.span}>
                      {GetString('LOG_SERVER')}
                    </span>
                  </InputPrefix>
                </>
              )}
              suffix={() => (
                <>
                  <InputSuffix>
                    <Button>
                      <CopyToClipboard
                        text={logsUploadPath.Path}
                        onCopy={() => setShowCopyToClipBoard(true)}
                      >
                        <ContentCopyIcon />
                      </CopyToClipboard>
                    </Button>
                  </InputSuffix>
                </>
              )}
              style={{ width: 380, height: 38 }}
            />
          </Grid>

          <Grid className={clsx(classes.logsContainer, classes.margin)}>
            <Grid item className={classes.gridItemMargin}>
              <Grid item xs={12}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'DATE_RANGE'}
                  gutterBottom
                />
              </Grid>
              <Grid className={classes.dateRangePickerGrid}>
                <Grid item className={classes.datePicker}>
                  <div className={classes.startDateMargin}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'START_DATE_RANGE_LABEL'}
                      gutterBottom
                    />
                  </div>
                  <DatePicker
                    className={classes.leftMargin}
                    id="start-date"
                    value={startDate}
                    onChange={e => setStartDate(e.value)}
                    width={210}
                    valid={startDate !== null}
                  ></DatePicker>
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={handleCheckboxChange}
                        name="checkedB"
                        color="primary"
                        checked={showEndDate}
                      />
                    }
                    label={
                      <StringResourceManager
                        variant="body1"
                        style="dialogLabel"
                        stringKey="END_DATE_RANGE_LABEL"
                        gutterBottom
                      />
                    }
                  />

                  {showEndDate && (
                    <DatePicker
                      id="end-date"
                      value={endDate}
                      min={startDate || undefined}
                      onChange={e => setEndDate(e.value)}
                      width={210}
                    ></DatePicker>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid className={clsx(classes.logsContainer, classes.margin)}>
            <Grid item className={classes.gridItemMargin}>
              <Grid item xs={12}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'SELECT_FREQUENCY'}
                  gutterBottom
                />
              </Grid>
              <Grid item xs={12}>
                <Cron value={cronFrequency} setValue={handleCronChange}></Cron>
              </Grid>
            </Grid>
          </Grid>

          <FileGrid
            className={classes.gridStyle}
            data={logFilesData}
            autoProcessData={true}
            dataItemKey={DATA_ITEM_KEY}
            onItemChange={itemChange}
            style={{ width: 800 }}
          >
            <GridToolbar>
              <Button themeColor="primary" onClick={handleAddFileRow}>
                {ADD_NEW}
              </Button>
            </GridToolbar>
            <GridColumn
              field="DirectoryName"
              title={GetString('COLUMN_TITLE_DIRECTORY')}
              cells={{ data: DirectoryCell }}
              width={300}
            ></GridColumn>
            <GridColumn
              field="FileName"
              title={GetString('COLUMN_TITLE_FILENAME')}
              cells={{ data: FileNameCell }}
              width={300}
            ></GridColumn>
            <GridColumn
              title={GetString('COLUMN_TITLE_ACTIONS')}
              cells={{ data: ActionCell }}
            ></GridColumn>
          </FileGrid>
          <div className={classes.actionButton}>
            <Button
              themeColor="primary"
              onClick={handleSendScheduleLogs}
              disabled={
                hasInvalidDirectoryPath(logFilesData) ||
                generateFilePaths(logFilesData) === '' ||
                startDate === null
              }
            >
              {SEND_SCHEDULE_LOGS_STR}
            </Button>
          </div>

          {nodeSelectionDialogVisible && (
            <ScheduledCommandNodeSelectionDialog
              handleCloseDialog={() => setNodeSelectionDialogVisible(false)}
              displayCommandSendNotification={() => displayCommandSend(true)}
              parameters={parameters}
              startDate={convertDateYYYYMMDD(startDate)}
              endDate={endDate ? convertDateYYYYMMDD(endDate) : null}
              cronFrequency={cronFrequency}
              title="UPLOAD_LOGS_PERIODICALLY"
              commandName="SCHEDULE_UPLOAD_LOGS"
            />
          )}
          <Divider />
          <ScheduledCommandHistory
            title="SCHEDULED_LOGS_REQUEST_HISTORY"
            commandName={commandName}
            onStartDateChange={onHistoryStartDateChange}
            onEndDateChange={onHistoryEndDateChange}
            onUseCurrentDateTimeFilterChange={onUseCurrentDateTimeFilterChange}
          />
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {commandSend && commandSuccessMessage !== '' && (
                <Notification
                  className={classes.send}
                  type={{
                    style:
                      commandSuccessMessage === 'success' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        commandSuccessMessage === 'success'
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : commandSuccessMessage === 'failed'
                          ? 'COMMAND_SEND_FAILED'
                          : ''
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showCopyToClipBoard && (
                <Notification
                  className={classes.copyToClipBoard}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                >
                  {<StringResourceManager stringKey={'COPY_TO_CLIPBOARD'} />}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
}
